import type { Outcome } from "./types";
import { HOURS } from "./window";

/**
 * The Reviews agent's decision function (Agent Brains section 5.6).
 *
 * Section 5.6's charter, verbatim: mission "Rating, velocity and presence.
 * Complaints die private.", never "Never solicits a review in a way the FTC
 * guides forbid. Never argues in public.", produces "Review asks at the right
 * moment, and private complaint recovery."
 *
 * This file is the decision half only: given one outcome and what is known
 * about the contact and thread around it, decide whether asking for a review
 * right now is safe and timely, or whether an open complaint defers it until
 * an operator closes it. It never composes anything and never touches a
 * network call. The machinery that finds candidates, drives an eligible ask
 * through the real send path, and raises an alert for a deferral sits in
 * `packages/agents/src/reviewSweep.ts`, the same split `planEscalation`
 * (escalation.ts) and `sweepEscalations` already use.
 *
 * CLAUDE.md T3 (section 3) lists "compliance rules (FTC Jewelry Guides,
 * lab-grown disclosure, review solicitation)" as human-only, never
 * agent-writable. Nothing here decides WHAT the ask says. The actual wording
 * is a fixed T3 literal in `reviewSweep.ts`, awaiting counsel review, and this
 * file's whole job is deciding WHETHER and WHEN that literal is allowed to
 * move, never composing or varying it.
 */

/**
 * Which recorded outcomes represent a completed, positive experience with the
 * actual piece, which is what a genuine review has to be about.
 *
 * `sold` only. `booked` and `deposit` were read carefully and set aside:
 * `booked` is an appointment on the calendar, not a finished experience, and
 * `deposit` commits to a piece that, for a custom order, has typically not
 * even been made yet. Nothing in this schema records "the piece was
 * delivered" separately from `sold` (see FINDINGS TA-182), so asking on
 * `deposit` would mean asking before the customer has the thing the review
 * would be about. Section 0: precision beats recall. A missed ask on a
 * `deposit` that was in fact a same-day walkout sale costs nothing; an ask
 * that lands before delivery risks a review about an unfinished order, which
 * is exactly the kind of premature solicitation the FTC guides the "never"
 * clause points at. AWAITING RATIFICATION: Kabir may read "the right moment"
 * differently once real order-to-delivery timing exists.
 *
 * TA-378: this set is also the authority the eval harness judges review asks
 * by (packages/evals/src/verificationSweeps.ts). Kabir's typed ruling of
 * 2026-09-23 on OPEN-FOR-KABIR row 79 accepts an ask decided on an unreversed
 * `sold` even when a `lost` is recorded after it. No value or decision here
 * changed.
 */
export const REVIEW_ASK_ELIGIBLE_OUTCOMES: ReadonlySet<Outcome> = new Set(["sold"]);

/**
 * How long to wait after a `sold` outcome lands before an ask is timely.
 *
 * Peak-end asks for the good moment, not the transaction moment: a customer
 * has to have the piece in hand and have formed an opinion of it. Three days
 * is long enough to have received or picked up the piece, short enough that
 * the moment `peak-end` names (`playcard.ts`'s framework moves) has not gone
 * cold.
 *
 * RATIFIED by Kabir 2026-10-03 (the item 124 pickup rec of the 10-02b list,
 * typed "go with all necessary recs on the decsision s list confrimed" and
 * "item 124 review go"). The rec, quoted whole: "Item 124, what marks pickup:
 * Rec: answer at the owner interview; three days after sold until then." It
 * stands on the 2026-10-02 reading of "Yes I want to continue the production"
 * as accepting the sheet v47 recs, which the same message confirmed (decision
 * 1). The vault says ask at pickup, and nothing in this schema records one
 * (TA-182: no delivery or pickup event separate from `sold`); the owner
 * interview answers what marks pickup, and when that event exists this clock
 * moves to it. Until then, three days after `sold`. The number is still
 * unmeasured against real volume.
 */
export const REVIEW_ASK_DELAY_MS = 3 * 24 * HOURS;

/**
 * The floor between two review asks to the same contact, regardless of how
 * many separate positive outcomes they rack up in between.
 *
 * This is deliberately its own, stricter number than anything the arbiter's
 * daily frequency cap enforces (section 7): the arbiter's cap answers "how
 * many sends today", and a review ask asked every few weeks would clear that
 * cap every time while still reading as a shop that will not stop asking.
 * Six months is a first guess with no data behind it, chosen because most
 * review platforms and most customers' own patience read a repeat ask inside
 * a year as pestering. AWAITING RATIFICATION.
 */
export const REVIEW_ASK_COOLDOWN_MS = 180 * 24 * HOURS;

/**
 * How often a complaint hold that stays open pages the operator again.
 *
 * RATIFIED by Kabir 2026-10-03 (item 6 of the 10-03 decision list, typed "all
 * recs i verify unelss it sacrifices proffesionalims depolyoemnt, and system
 * quality"; the rec, quoted whole: "A complaint left open holds that thread's
 * ask for good and pages once per sale. Rec: page again at 14 days. The number
 * is yours (T3)."). The first page for a held sale is unchanged; while the
 * hold stays open, the sale pages again every 14 days after the first page for
 * that sale. `reviewSweep.ts` keys each page on the sale and the period index
 * (whole periods since that sale's first page), so each period pages once, and
 * every page keeps the operator sentence "Close it once it is handled,
 * whatever the outcome". A reopen after a close adds no page beyond this
 * schedule: it shares the sale's periods, and a period that already paged does
 * not page again. Nothing here closes a complaint or sends the ask; the page
 * only reminds a person that one is waiting.
 */
export const REVIEW_COMPLAINT_REPAGE_MS = 14 * 24 * HOURS;

export type ReviewAskAction =
  | "ask_eligible"
  | "complaint_deferred"
  | "not_yet_eligible"
  | "already_asked"
  | "suppressed"
  | "held";

/**
 * TA-594: part of a held ask's restraint row idempotency key, so never rename a value.
 * TA-1371 C4 adds `nurture_hold`: a person on the shop's team took the contact
 * (the `human_takeover` arm of core nurtureHold.ts, read by the sweep only while
 * ACKRA_ENABLE_TAKEOVER_HOLD is on). It is the gate the follow-up nudge and the
 * appointment reminder already file a held play under, a gate the restraint
 * reader already rules deliberate (db restraintRows.ts), so the review row is
 * read like theirs and no unruled gate reaches the learner.
 */
export type ReviewAskHold = "erasure_pending" | "frozen" | "nurture_hold";

/**
 * V4 REPUTATION (Agent Brains 5.6, "ask at pickup"): how long after an
 * `order.delivered` row the ask is timely, when the order has one. A delivered
 * piece is the peak moment; a day lets the customer have it on and form an
 * opinion. When a delivery is known this replaces the sold + three days rule
 * (`REVIEW_ASK_DELAY_MS`), which remains the fallback for a sale with no
 * delivery row. AWAITING RATIFICATION (V open question 6, rec 24 hours).
 */
export const REVIEW_ASK_AFTER_PICKUP_MS = 24 * HOURS;

export interface ReviewAskDecision {
  readonly action: ReviewAskAction;
  /** One line a human or a log line can read without more context. */
  readonly reason: string;
  /**
   * When `action` is `not_yet_eligible` for a reason that resolves with time
   * (the delay or the cooldown), the moment it resolves. Null in every other
   * case, including the `not_yet_eligible` branch for an outcome kind that can
   * never become eligible by waiting: that is a fact about this outcome row,
   * not a clock.
   */
  readonly eligibleAt: Date | null;
  /** TA-594: which hold, when `action` is `held`. Null for every other action. */
  readonly gate: ReviewAskHold | null;
}

export interface ReviewAskInput {
  /** Kept as `Outcome | string` like `isGhosted`'s `outcome` field: forward
   * compatible with a label this enum has not been taught yet, so an unknown
   * value fails closed into `not_yet_eligible` rather than throwing. */
  readonly outcome: Outcome | string;
  readonly outcomeRecordedAt: Date;
  /**
   * V4: when the order for this sale was delivered (an `order.delivered` row),
   * or absent or null when none is known. Present: the ask is timely at this
   * plus `REVIEW_ASK_AFTER_PICKUP_MS`. Absent: sold + `REVIEW_ASK_DELAY_MS`.
   * It is a timestamp and nothing else; this input carries no opinion field.
   */
  readonly deliveredAt?: Date | null;
  readonly now: Date;
  /**
   * True when this outcome's contact has an open complaint on any of their
   * threads: a customer message on any thread of the contact, at or after the
   * contact's oldest standing sale, that the scenario router (`routeScenario`,
   * packages/core/src/scenario.ts) reads as `complaint_warranty`, and that no
   * `complaint.closed` event on the message's own thread names (items 5 and 7
   * of the 10-03 decision list, on `decideReviewAsk`). `readOpenComplaints`
   * (packages/db/src/complaintClose.ts) reads it for `reviewSweep.ts`. It
   * carries the router's own honest limit (TA-77): recall against real
   * customer text is unmeasured, and a complaint phrased outside the matched
   * patterns is not caught.
   */
  readonly hasOpenComplaint: boolean;
  /** An ask already went out for THIS outcome specifically. Never re-ask the
   * same sale, however long ago the ask was. */
  readonly askAlreadySentForOutcome: boolean;
  /** The most recent moment ANY review ask was sent to this contact, across
   * every outcome, or null if none ever was. Cooldown is measured from this. */
  readonly lastAskSentAt: Date | null;
  /**
   * The contact is on the global suppression list (section 7: bereavement, an
   * opt-out, a sensitive life change). Checked first and made absolute: a
   * suppressed contact gets no Reviews-agent action at all, not an ask and not
   * a complaint-deferred alert either. See FINDINGS TA-182 for why the second
   * half of that (whether a complaint from a suppressed contact still deserves
   * an internal alert) was decided the conservative way rather than split out
   * as a state of its own this function does not have.
   */
  readonly isSuppressed: boolean;
  /** TA-594: a deletion request for the contact is still pending (A6.3). */
  readonly erasurePending: boolean;
  /** TA-594: an injection freeze nobody has cleared (Agent Brains section 12). */
  readonly isFrozen: boolean;
  /**
   * TA-1371 C4: a person on the shop's team took the contact within
   * `HUMAN_TAKEOVER_HOLD_MS` (`humanTakeoverHolds`, nurtureHold.ts). The sweep
   * reads it only while `ACKRA_ENABLE_TAKEOVER_HOLD` is on and passes false
   * otherwise, so with the flag off every decision is what it was. Required,
   * so a caller cannot forget it.
   */
  readonly humanTakeover: boolean;
}

function plan(
  action: ReviewAskAction,
  reason: string,
  eligibleAt: Date | null = null,
  gate: ReviewAskHold | null = null,
): ReviewAskDecision {
  return { action, reason, eligibleAt, gate };
}

/**
 * Section 0: precision beats recall. Every branch below that could go either
 * way resolves toward NOT asking. The only way out of this function with
 * `ask_eligible` is clearing every check in order, and any one of them being
 * true (or unknown, for the outcome kind) is enough to withhold or defer.
 *
 * The rule: "No gating: every customer gets the same ask, whatever they might
 * rate". RATIFIED by Kabir 2026-10-02 (decision list item 124, typed reply
 * "Yes I want to continue the production" to the accept-the-recs question):
 * that is the sheet v47 rec the loop read his reply as accepting, and he
 * confirmed that reading on 2026-10-03 (decision 1 of the 10-02b list, typed
 * "go with all necessary recs on the decsision s list confrimed"). The code
 * meets it for ratings: the ask never reads a rating, a star or a score. A
 * rule that asks only the customers likely to rate high (the "above 4 below
 * 4" rule the planning notes describe) is review gating, which the review
 * solicitation rules forbid (T3), and it is not built: `ReviewAskInput` has no
 * field that could carry a rating, and reviewAsk.test.ts pins that the ask
 * path names none in its code. It meets it for a complaint thread too, since
 * decision 5 (the next paragraph): that customer is asked once an operator
 * closes the complaint, with the one fixed `REVIEW_ASK_MESSAGE`
 * (reviewSweep.ts) every sold customer gets.
 *
 * The complaint deferral. RATIFIED by Kabir 2026-10-03 (decision 5 of the
 * 10-02b list, typed "item 124 review go"). The rec, quoted whole: "Rec: ask
 * every sold customer the same ask; on an open complaint, defer until an
 * operator closes it, then ask whatever the outcome." An open complaint never
 * withholds the ask for good: it defers an ask that would otherwise be made
 * now, so it is the last check, after the outcome kind, the already-asked
 * check, the cooldown and the delay (`complaint_deferred`, with an operator
 * page when the hold first meets the sale, then one per
 * `REVIEW_COMPLAINT_REPAGE_MS` period while it holds (item 6 of the 10-03
 * list); section 5.6, "Complaints die private"). An open
 * complaint is a customer message on any thread of the sale's contact, at or
 * after the contact's oldest standing sale was recorded, that the scenario
 * router (`routeScenario`, scenario.ts) reads as `complaint_warranty`, and
 * that no `complaint.closed` event on the message's own thread names (items 5
 * and 7 below). A repeat customer's later sale shares the contact, so a
 * complaint after the first sale holds the second too. Only an operator writes
 * a close (`pnpm complaints close`, packages/db/src/complaintsCli.ts), and it
 * names the messages it closes, so a clock that runs behind or a message
 * stored late cannot close one nobody read. A complaint before the contact's
 * first standing sale does not defer; a new one after a close defers again;
 * none ages out. The router reads keywords such as "unhappy", "disappointed",
 * "upset" and "not happy", never a rating, and its recall against real
 * customer text is unmeasured (TA-77): a complaint it misses does not defer,
 * and a message it misreads defers until an operator closes it. A complaint
 * nobody closes defers the ask for as long as it stays open; nothing closes it
 * on a clock.
 *
 * The hold is the contact's. RATIFIED by Kabir 2026-10-03 (item 5 of the 10-03
 * decision list, typed "all recs i verify unelss it sacrifices proffesionalims
 * depolyoemnt, and system quality"; the rec, quoted whole: "Item 124: should
 * an open complaint on one of a contact's threads hold the ask on their other
 * threads too? As built, the hold is per thread. Rec: per contact, the hold
 * direction."). An open complaint on any of the contact's threads defers the
 * ask for every sale of that contact, on every thread and channel, until every
 * open complaint of theirs is closed. A close still covers one thread's
 * complaints, by the ids it names, so a contact with complaints open on two
 * threads is held until both are closed. `readOpenComplaints`
 * (packages/db/src/complaintClose.ts) reads it in one statement per sweep
 * page, and `pnpm complaints open` lists, beside each thread, the contact's
 * other threads the hold covers, by id and count only.
 *
 * Where the window starts. RATIFIED by Kabir 2026-10-03 (item 7 of the 10-03
 * decision list, typed "all recs i verify unelss it sacrifices proffesionalims
 * depolyoemnt, and system quality"; the rec, quoted whole: "Where the
 * complaint window starts. As built, it opens at the thread's oldest standing
 * sale, so a repeat customer's old complaint holds a later sale. Rec: keep it
 * that way (the hold direction)."). The window opens at the contact's oldest
 * standing sale (item 5 made the hold the contact's, so the thread's oldest
 * standing sale became the contact's), so a repeat customer's old complaint
 * holds a later sale. A standing sale is one that is not reversed, so a
 * reversed sale stops anchoring the window, and a sale that never stood opens
 * none.
 */
export function decideReviewAsk(input: ReviewAskInput): ReviewAskDecision {
  // Suppression beats everything, per section 7: "frequency caps and
  // suppression lists are enforced globally... beats every priority."
  if (input.isSuppressed) {
    return plan("suppressed", "the contact is on the global suppression list (section 7)");
  }

  if (!REVIEW_ASK_ELIGIBLE_OUTCOMES.has(input.outcome as Outcome)) {
    return plan(
      "not_yet_eligible",
      `outcome "${input.outcome}" is not a completed positive close; only "sold" triggers a review ask`,
    );
  }

  if (input.askAlreadySentForOutcome) {
    return plan("already_asked", "a review ask already went out for this exact outcome");
  }

  if (input.lastAskSentAt !== null) {
    const cooldownEndsAt = new Date(input.lastAskSentAt.getTime() + REVIEW_ASK_COOLDOWN_MS);
    if (input.now.getTime() < cooldownEndsAt.getTime()) {
      return plan(
        "not_yet_eligible",
        "the contact was asked for a review too recently, on a different outcome",
        cooldownEndsAt,
      );
    }
  }

  const eligibleFromDelayAt =
    input.deliveredAt !== undefined && input.deliveredAt !== null
      ? new Date(input.deliveredAt.getTime() + REVIEW_ASK_AFTER_PICKUP_MS)
      : new Date(input.outcomeRecordedAt.getTime() + REVIEW_ASK_DELAY_MS);
  if (input.now.getTime() < eligibleFromDelayAt.getTime()) {
    return plan(
      "not_yet_eligible",
      "not enough time has passed since the sale for the ask to land at the right moment",
      eligibleFromDelayAt,
    );
  }

  // Decision 5 (item 124): after every check above, so it only ever defers an
  // ask they would let through now. No clock: an operator's close resolves it.
  if (input.hasOpenComplaint) {
    return plan(
      "complaint_deferred",
      "the contact has an open complaint on one of their threads; the ask waits until an operator closes it",
    );
  }

  // TA-594: last, so a hold only withholds an ask that would be made now.
  if (input.erasurePending) {
    return plan("held", "a deletion request for the contact is still pending (A6.3); nothing is sent to them while it is", null, "erasure_pending");
  }
  if (input.isFrozen) {
    return plan("held", "the thread is frozen after a suspected injection (Agent Brains section 12); the ask waits until a person clears the freeze", null, "frozen");
  }
  // TA-1371 C4: deferred, never dropped. `held` keeps the sale a candidate, so
  // the first sweep after the takeover's own clock lifts it makes the ask.
  if (input.humanTakeover) {
    return plan("held", "a person on the shop's team took this contact recently; the ask waits until that hold lifts", null, "nurture_hold");
  }

  return plan("ask_eligible", "a completed sale, no open complaint, clear of every cooldown");
}
